function cleanText(value, max = 240) {
  return String(value || '').replace(/\s+/g, ' ').trim().slice(0, max);
}

function includesAny(text, terms) {
  return terms.some((term) => text.includes(term));
}

function firstSentence(value, max = 160) {
  const text = cleanText(value, max * 2);
  if (!text) return '';
  const match = text.match(/(.+?[.!?])(\s|$)/);
  return cleanText(match?.[1] || text, max);
}

function pickIssue(workspace) {
  const issue = (workspace?.agent_context_bus?.issue_list || [])[0];
  return cleanText(issue?.title || issue?.description, 120);
}

function pickAuthority(workspace) {
  const authority = (workspace?.agent_context_bus?.trusted_sources || [])[0];
  return cleanText(authority?.title, 140);
}

function pickObjective({ userPrompt, workspace }) {
  return cleanText(
    workspace?.agent_context_bus?.objective?.text
      || workspace?.handoff_context?.current_objective
      || firstSentence(userPrompt, 180),
    180,
  );
}

function getTrustCounts(workspace) {
  return workspace?.agent_context_bus?.summary?.trust_counts || {};
}

function uniqueSteps(steps) {
  const seen = new Set();
  return steps.filter((step) => {
    const key = `${step.target}::${step.routeHint || ''}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function inferChatTaskShape({
  userPrompt = '',
  assistantResponse = '',
  mode = 'RESEARCH',
} = {}) {
  const text = `${userPrompt}\n${assistantResponse}`.toLowerCase();
  const normalizedMode = String(mode || 'RESEARCH').toUpperCase();

  const voiceLike = includesAny(text, [
    'dictation',
    'dictate',
    'voice note',
    'audio',
    'call recording',
    'client call',
    'hearing debrief',
    'spoken',
    'transcribe',
    'transcript',
  ]);
  const chronologyLike = includesAny(text, [
    'chronology',
    'timeline',
    'sequence of events',
    'procedural history',
    'list the dates',
    'brief facts',
    'what happened',
  ]);
  const factualIntakeLike = chronologyLike || includesAny(text, [
    'client meeting',
    'client call',
    'hearing debrief',
    'facts are messy',
    'organise the facts',
    'sort the facts',
    'background first',
    'factual matrix',
  ]);
  const draftingLike = normalizedMode === 'DRAFT' || includesAny(text, [
    'draft',
    'petition',
    'reply',
    'notice',
    'written submission',
    'written statement',
    'affidavit',
    'grounds',
    'pleading',
    'filing',
  ]);
  const hearingLike = normalizedMode === 'STRATEGY' || includesAny(text, [
    'hearing',
    'oral submissions',
    'oral arguments',
    'bench',
    'judge',
    'interim relief',
    'tomorrow',
    'objection',
    'rebuttal',
  ]);
  const weaknessLike = includesAny(text, [
    'red team',
    'weakness',
    'attack',
    'challenge',
    'what can go wrong',
    'best attack',
    'pressure-test',
    'pressure test',
    'opposing counsel',
    'weak points',
  ]);
  const explainLike = normalizedMode === 'EXPLAIN' || includesAny(text, [
    'explain',
    'simple terms',
    'client update',
    'translate',
    'hindi',
    'tamil',
    'telugu',
    'marathi',
    'plain language',
  ]);
  const authorityLike = normalizedMode === 'RESEARCH' || includesAny(text, [
    'research',
    'case law',
    'authorities',
    'precedent',
    'ratio',
    'section',
    'statute',
    'maintainability',
    'jurisdiction',
    'citation',
  ]);
  const clientSafeLike = includesAny(text, [
    'client update',
    'client-ready',
    'plain language',
    'simple terms',
    'send to client',
  ]);

  let primary = 'LEGAL_RESEARCH';
  if (voiceLike) primary = 'VOICE_INTAKE';
  else if (factualIntakeLike) primary = 'FACT_INTAKE';
  else if (draftingLike) primary = 'DRAFTING';
  else if (hearingLike) primary = 'HEARING_PREP';
  else if (weaknessLike) primary = 'RED_TEAM';
  else if (explainLike) primary = 'EXPLAINER';

  return {
    primary,
    needsAuthorities: authorityLike || hearingLike || draftingLike,
    needsDrafting: draftingLike,
    needsBenchPrep: hearingLike,
    needsWeaknessCheck: weaknessLike || draftingLike || hearingLike,
    needsChronology: chronologyLike || voiceLike || factualIntakeLike,
    needsIssueMap: authorityLike || hearingLike || factualIntakeLike,
    needsVoice: voiceLike,
    needsTranslation: explainLike && includesAny(text, ['translate', 'hindi', 'tamil', 'telugu', 'marathi']),
    needsClientSafeLanguage: clientSafeLike || (explainLike && !authorityLike),
  };
}

export function buildChatWorkflowPlan({
  userPrompt = '',
  assistantResponse = '',
  mode = 'RESEARCH',
  workspace = null,
} = {}) {
  const shape = inferChatTaskShape({ userPrompt, assistantResponse, mode });
  const issueHint = pickIssue(workspace);
  const authorityHint = pickAuthority(workspace);
  const objective = pickObjective({ userPrompt, workspace });
  const trustCounts = getTrustCounts(workspace);
  const hasTrustedAuthorities = Number(trustCounts.trusted_sources || 0) > 0;
  const hasMatterFiles = Number(trustCounts.matter_files || 0) > 0;
  const hasReviewedVoice = Number(trustCounts.reviewed_jolly_memory || 0) > 0;
  const steps = [];

  const addStep = (step) => {
    steps.push({
      prefillType: 'TOPIC',
      focusIssue: issueHint,
      focusAuthority: authorityHint,
      ...step,
    });
  };

  if (shape.primary === 'VOICE_INTAKE') {
    addStep({
      target: 'JOLLY',
      agent: 'Jolly',
      title: 'Capture the factual record cleanly',
      reason: 'Use Jolly first when the work starts from speech, dictation, or a live debrief that should become reviewed matter memory.',
    });
    addStep({
      target: 'SABHA',
      agent: 'Sabha',
      title: 'Stabilise chronology, issues, and strategy',
      reason: 'Once the voice record is captured, Sabha is the fastest way to turn that intake into a usable chronology, issue map, and matter path.',
    });
    addStep({
      target: 'RESEARCH_AGENT',
      agent: 'Research',
      title: 'Pull the exact authorities for the pressure points',
      reason: 'Move into Research only after the intake is clean enough to narrow the real issues and forum-specific support.',
    });
    if (shape.needsDrafting) {
      addStep({
        target: 'VAKALAT',
        agent: 'Vakalat',
        title: 'Move the cleaned brief into the drafting file',
        reason: 'Once the voice-led intake and authority stack are stable, move straight into the live drafting surface.',
      });
    }
  } else if (shape.primary === 'FACT_INTAKE') {
    if (!hasReviewedVoice && includesAny(`${userPrompt}\n${assistantResponse}`.toLowerCase(), ['call', 'meeting', 'debrief'])) {
      addStep({
        target: 'JOLLY',
        agent: 'Jolly',
        title: 'Capture the live factual narrative before it drifts',
        reason: 'This looks like live intake or a debrief, so Jolly should lock the record before other desks build on it.',
      });
    }
    addStep({
      target: 'SABHA',
      agent: 'Sabha',
      title: 'Turn the facts into chronology and decision points',
      reason: 'Sabha is the best next desk when the facts are messy and the lawyer needs chronology, issue framing, and the practical fork in the road.',
    });
    addStep({
      target: 'RESEARCH_AGENT',
      agent: 'Research',
      title: 'Check which legal pressure points actually hold',
      reason: 'Once the facts are stabilised, Research can narrow the governing issue, authority stack, and forum-specific risk.',
    });
    if (shape.needsWeaknessCheck) {
      addStep({
        target: 'AINA',
        agent: 'Aina',
        title: 'Pressure-test the fact pattern before relying on it',
        reason: 'Aina helps once chronology and law are visible and you want the fastest weak-point sweep on the current theory.',
      });
    }
  } else if (shape.primary === 'DRAFTING') {
    if (hasTrustedAuthorities && hasMatterFiles) {
      addStep({
        target: 'VAKALAT',
        agent: 'Vakalat',
        title: 'Move the draft into the live drafting surface',
        reason: 'The matter already has enough authority and record support that the next high-value move is to draft on the shared file itself.',
      });
      addStep({
        target: 'AINA',
        agent: 'Aina',
        title: 'Stress-test the draft before it leaves chamber',
        reason: 'Aina is the right second pass when the draft exists and you want weakness-finding before filing or service.',
      });
      addStep({
        target: 'RESEARCH_AGENT',
        agent: 'Research',
        title: 'Backfill any exposed authority gaps',
        reason: 'Use Research last here to patch only the narrow gaps that survive after drafting and red-team review.',
      });
    } else {
    addStep({
      target: 'RESEARCH_AGENT',
      agent: 'Research',
      title: 'Lock the authority stack before hard drafting',
      reason: 'Research is the best first move when the filing still needs verified support, safer framing, or narrower grounds.',
    });
    addStep({
      target: 'VAKALAT',
      agent: 'Vakalat',
      title: 'Move the draft into the live drafting file',
      reason: 'Use the shared drafting surface once the brief and authorities are stable enough to turn into a chamber-ready document.',
    });
    addStep({
      target: 'AINA',
      agent: 'Aina',
      title: 'Stress-test the filing before it leaves chamber',
      reason: 'Aina is the right last pass when you want weakness-finding, bench pressure, or opposing-counsel attacks before filing.',
    });
    }
  } else if (shape.primary === 'HEARING_PREP') {
    addStep({
      target: 'SABHA',
      routeHint: 'bench',
      agent: 'Judge Strategy',
      title: 'Pressure-test the hearing line and bench posture',
      reason: 'Start inside Sabha when the matter needs hearing structure, likely bench questions, or judge-strategy style preparation.',
    });
    addStep({
      target: 'RESEARCH_AGENT',
      agent: 'Research',
      title: 'Tighten the authorities for each live point',
      reason: 'After the hearing sequence is clear, Research should narrow the best verified authorities for each objection and fallback ask.',
    });
    addStep({
      target: 'AINA',
      agent: 'Aina',
      title: 'Red-team the oral line before court',
      reason: 'Finish in Aina when you want the fastest weak-point sweep on the final hearing position.',
    });
  } else if (shape.primary === 'RED_TEAM') {
    addStep({
      target: 'AINA',
      agent: 'Aina',
      title: 'Run the adversarial stress test first',
      reason: 'This looks like weakness-finding or opposition prep, so Aina should take the next turn immediately.',
    });
    addStep({
      target: 'RESEARCH_AGENT',
      agent: 'Research',
      title: 'Backfill the exposed weak points with verified support',
      reason: 'Research belongs after the attack surface is visible so the follow-up authority search is narrower and more useful.',
    });
    addStep({
      target: 'SABHA',
      agent: 'Sabha',
      title: 'Rebuild the stronger strategic sequence',
      reason: 'Sabha is useful once you know the weak points and need the best sequence, fallback asks, and matter-wide framing.',
    });
  } else {
    addStep({
      target: 'RESEARCH_AGENT',
      agent: 'Research',
      title: 'Turn this into a verified authority trail',
      reason: 'Research is the best next move when the issue still needs cleaner authority, issue mapping, or forum-specific narrowing.',
    });
    addStep({
      target: 'SABHA',
      agent: 'Sabha',
      title: shape.needsChronology ? 'Turn the law into chronology and use in the matter' : 'Move from law to strategic use in the matter',
      reason: shape.needsChronology
        ? 'Once the core law is clearer, Sabha should stabilise chronology, issue pressure, and how the legal position fits the actual record.'
        : 'Sabha should follow once the legal issue is clearer and the file needs chronology, issue pressure, or hearing strategy.',
    });
    addStep({
      target: 'AINA',
      agent: 'Aina',
      title: 'Pressure-test the answer before you rely on it',
      reason: 'Aina is a strong third step when the file needs weakness-finding before drafting, advising, or arguing from this position.',
    });
  }

  const dedupedSteps = uniqueSteps(steps).slice(0, 4);
  const firstAgent = dedupedSteps[0]?.agent || 'the next desk';
  const pathSummary = dedupedSteps.map((step) => step.agent).join(' -> ');

  return {
    frame_label: {
      VOICE_INTAKE: 'Voice-first intake',
      FACT_INTAKE: 'Factual intake',
      DRAFTING: 'Drafting workflow',
      HEARING_PREP: 'Hearing prep',
      RED_TEAM: 'Weak-point analysis',
      EXPLAINER: 'Explain and simplify',
      LEGAL_RESEARCH: 'Issue and authority work',
    }[shape.primary] || 'Matter workflow',
    objective: objective || 'Move this thread into the right next desk without losing continuity.',
    summary: cleanText(
      objective
        ? `${firstAgent} first for ${objective.toLowerCase()}`
        : `Best next path: ${pathSummary || 'continue inside the right desk'}`,
      160,
    ),
    steps: dedupedSteps,
    task_shape: shape,
  };
}
